import React, { forwardRef, useEffect, useState } from "react";
import {
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { FaHeadset } from "react-icons/fa";
import HelpDeskContent from "../../../components/HelpDeskContent";
import CopilotUpgradeButtons from "../../../components/CopilotUpgradeButtons";
import { OPEN_HELP_DESK_POST_MESSAGE_TYPE } from "../../../utils/helpDesk";
import { UserPreferencesLoader } from "./ExternalRecordingPage/UserPreferencesLoader";
import { SupabaseBootstrapEffect } from "./ExternalRecordingPage/SupabaseBootstrapEffect";
import { ExternalRecordingWindowHost } from "./ExternalRecordingPage/ExternalRecordingWindowHost";
import { BACKUP_INTERVAL_MS } from "../Constants";

/**
 * Copilot clinical tabs (A&P, Consider, Scores, Report) for the external recorder popup:
 * - While recording, tabs refresh from the live transcript via POST /api/generate-ai-recap/
 *   with model_choice 0 (Haiku) for low latency (see `useExternalRecording` injected script).
 * - After the audio is saved (or final transcript is ready), the same endpoint is called with
 *   model_choice 1 (Sonnet) for higher-quality tabs; the main app may also run `generateRecap`
 *   with Sonnet when live transcript text is present on save (AudioRecorder.js).
 */
// Patient audio list + upload UI are built in `useExternalRecording` (inject HTML/CSS/JS).
// Styled like the dashboard Audio Manager; current-audios list is scoped to the visit date /
// `{schema}/{year}/{date_}` paths (same as PatientPage.visibleAudios + AudioManagerModal).

export const ExternalRecordingPage = forwardRef((props, ref) => {
  const [preferredMicDeviceId, setPreferredMicDeviceId] = useState("");
  const [userSchemaName, setUserSchemaName] = useState("public");
  const [aiRecapEnabled, setAiRecapEnabled] = useState(true);
  const [copilotEnabled, setCopilotEnabled] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [audioBackupIntervalMs, setAudioBackupIntervalMs] = useState(
    BACKUP_INTERVAL_MS,
  );
  const {
    isOpen: isHelpDeskOpen,
    onOpen: openHelpDesk,
    onClose: closeHelpDesk,
  } = useDisclosure();

  useEffect(() => {
    const onMessage = (event) => {
      if (event?.data?.type === OPEN_HELP_DESK_POST_MESSAGE_TYPE) {
        openHelpDesk();
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [openHelpDesk]);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(
        "[ExternalRecordingPage] Host mounted in main app — popup content/logs are in useExternalRecording (injected HTML/JS), not this file.",
      );
    }
  }, []);

  return (
    <>
      {preferencesLoaded && !copilotEnabled && (
        <div
          style={{
            padding: "8px 12px",
            textAlign: "center",
            background: "#fffbeb",
            borderBottom: "1px solid #fcd34d",
            fontSize: "0.875rem",
            color: "#92400e",
          }}
        >
          <div style={{ marginBottom: "6px" }}>
            Copilot is not enabled on your plan.
          </div>
          <CopilotUpgradeButtons display="inline-block" marginBottom="6px" />
          <div
            style={{
              fontSize: "0.8125rem",
              color: "#78350f",
              marginBottom: "8px",
            }}
          >
            If you need help upgrading your plan, use Help Desk to send a
            message for support.
          </div>
          <Button
            type="button"
            size="xs"
            colorScheme="red"
            leftIcon={<Icon as={FaHeadset} />}
            onClick={(e) => {
              e.preventDefault();
              openHelpDesk();
            }}
          >
            Help Desk
          </Button>
        </div>
      )}
      <Modal
        isOpen={isHelpDeskOpen}
        onClose={closeHelpDesk}
        size="lg"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Help Desk</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <HelpDeskContent
              isActive={isHelpDeskOpen}
              onSubmitted={closeHelpDesk}
            />
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="ghost" onClick={closeHelpDesk}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <UserPreferencesLoader
        setPreferredMicDeviceId={setPreferredMicDeviceId}
        setUserSchemaName={setUserSchemaName}
        setAiRecapEnabled={setAiRecapEnabled}
        setAudioBackupIntervalMs={setAudioBackupIntervalMs}
        setCopilotEnabled={setCopilotEnabled}
        onLoaded={() => setPreferencesLoaded(true)}
      />
      <SupabaseBootstrapEffect />
      <ExternalRecordingWindowHost
        {...props}
        ref={ref}
        preferredMicDeviceId={preferredMicDeviceId}
        userSchemaName={userSchemaName}
        aiRecapEnabled={aiRecapEnabled}
        copilotEnabled={copilotEnabled}
        audioBackupIntervalMs={audioBackupIntervalMs}
        shouldOpenImmediately={
          !!props.shouldOpenImmediately && preferencesLoaded
        }
      />
    </>
  );
});

ExternalRecordingPage.displayName = "ExternalRecordingPage";

export default ExternalRecordingPage;
